import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Policy as PolicyIcon,
  Warning as WarningIcon,
  RequestQuote as QuoteIcon,
  Description as DraftIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  SpaceDashboard as DashboardIcon,
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import policyService from '../services/policyService';
import { formatApiError } from '../utils/errorHandling';
import { DashboardMetrics } from '../types/policy';
import { prefetchDashboardLists } from '../utils/prefetch';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kpiData, setKpiData] = useState<DashboardMetrics | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Prefetch likely navigation targets after dashboard loads
  useEffect(() => {
    if (!loading && kpiData) {
      // Prefetch in background (non-blocking)
      prefetchDashboardLists(queryClient).catch(console.error);
    }
  }, [loading, kpiData, queryClient]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await policyService.getDashboardMetrics();
      setKpiData(data);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(formatApiError(err));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('th-TH').format(value);
  };

  const getInsuranceTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      MOTOR: 'รถยนต์',
      HEALTH: 'สุขภาพ',
      LIFE: 'ชีวิต',
      FIRE: 'อัคคีภัย',
      MARINE: 'ทางทะเล',
      PROPERTY: 'ทรัพย์สิน',
      MISC: 'เบ็ดเตล็ด',
      life: 'ประกันชีวิต',
      non_life: 'ประกันวินาศภัย',
      motor: 'รถยนต์',
      fire_and_risk: 'อัคคีภัยและภัยเสี่ยง',
      miscellaneous: 'เบ็ดเตล็ด',
      marine: 'ทางทะเล',
      compulsory: 'พรบ',
      group: 'กลุ่ม',
      ordinary: 'สามัญ',
      industrial: 'อุตสาหกรรม',
      accident: 'อุบัติเหตุ',
      pa: 'อุบัติเหตุส่วนบุคคล (PA)',
      personal_accident: 'อุบัติเหตุส่วนบุคคล',
      travel: 'การเดินทาง',
      health: 'สุขภาพ',
      property: 'ทรัพย์สิน',
      rider: 'สัญญาเพิ่มเติม',
      unspecified: 'ไม่ระบุประเภท',
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      draft: 'ร่าง',
      sent: 'ส่งแล้ว',
      buy: 'ซื้อ',
      accepted: 'ตอบรับ',
      rejected: 'ปฏิเสธ',
      expired: 'หมดอายุ',
      converted: 'แปลงแล้ว',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
    const colors: { [key: string]: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' } = {
      draft: 'default',
      sent: 'info',
      buy: 'success',
      accepted: 'success',
      rejected: 'error',
      expired: 'warning',
      converted: 'primary',
    };
    return colors[status] || 'default';
  };

  const getCustomerTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      individual: 'บุคคล',
      corporate: 'นิติบุคคล',
      unspecified: 'ไม่ระบุ',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', width: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h4" gutterBottom>
          แดชบอร์ด
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            กรุณาตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์หรือลองใหม่อีกครั้ง
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!kpiData) {
    return null;
  }

  // Prepare chart data
  const policiesByType = kpiData.policies_by_type || {};
  const salesByTypeData = Object.entries(policiesByType).map(([type, count]) => ({
    name: getInsuranceTypeLabel(type),
    value: count as number,
  }));

  const topAgentsData = (kpiData.top_agents || []).map((agent) => ({
    agent_id: agent.agent_name,
    name: agent.agent_name,
    policies: agent.policy_count,
    premium: agent.total_premium,
  }));

  const recentQuotations = kpiData.recent_quotations || [];
  const salesByCompanyRaw = kpiData.sales_by_company || [];
  // Limit to top 10 companies for display
  const salesByCompanyData = salesByCompanyRaw.slice(0, 10).map((item) => ({
    name: item.company.length > 20 ? item.company.substring(0, 18) + '…' : item.company,
    fullName: item.company,
    total: item.total,
    life: item.by_type['life'] || 0,
    non_life: item.by_type['non_life'] || 0,
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        icon={<DashboardIcon color="primary" />}
        title="แดชบอร์ด"
        subtitle="Dashboard — ภาพรวมระบบและตัวชี้วัดสำคัญ"
      />

      <Box sx={{ mb: 4 }}>

        {/* KPI Cards Row 1: Policy KPIs */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
          <Card sx={{ flex: '1 1 calc(25% - 18px)', minWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 220 } }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PolicyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  กรมธรรม์ที่ใช้งาน
                </Typography>
              </Box>
              <Typography variant="h3" color="primary">
                {formatNumber(kpiData.active_policies_count)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                รายการ
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(25% - 18px)', minWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 220 } }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MoneyIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  เบี้ยประกันรายเดือน
                </Typography>
              </Box>
              <Typography variant="h3" color="success.main">
                {formatCurrency(kpiData.monthly_premium_total)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                บาท
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(25% - 18px)', minWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 220 } }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  ค่าคอมมิชชั่นค้างจ่าย
                </Typography>
              </Box>
              <Typography variant="h3" color="warning.main">
                {formatCurrency(kpiData.unpaid_commission_total)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                บาท
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(25% - 18px)', minWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 220 } }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="text.secondary">
                  กรมธรรม์ใกล้หมดอายุ
                </Typography>
              </Box>
              <Typography variant="h3" color="error.main">
                {formatNumber(kpiData.policies_nearing_expiry_count)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                รายการ (30 วัน)
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* KPI Cards Row 2: Quotation KPIs */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          <Card sx={{ flex: '1 1 calc(50% - 12px)', minWidth: { xs: '100%', sm: 220 }, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <QuoteIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6" color="text.secondary">
                  ใบเสนอราคาทั้งหมด
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#1976d2' }}>
                {formatNumber(kpiData.total_quotations_count)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                รายการ
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: '1 1 calc(50% - 12px)', minWidth: { xs: '100%', sm: 220 }, borderLeft: '4px solid #ed6c02' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DraftIcon sx={{ mr: 1, color: '#ed6c02' }} />
                <Typography variant="h6" color="text.secondary">
                  ใบเสนอราคา (ร่าง)
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ color: '#ed6c02' }}>
                {formatNumber(kpiData.draft_quotations_count)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                รายการ รอดำเนินการ
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Charts Row */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
          <Paper sx={{ flex: '1 1 calc(50% - 12px)', minWidth: { xs: '100%', md: 400 }, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              ยอดขายตามประเภทประกัน
            </Typography>
            {salesByTypeData.length > 0 ? (
              <Box sx={{ height: { xs: 220, md: 300 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={salesByTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {salesByTypeData.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">ไม่มีข้อมูล</Typography>
              </Box>
            )}
          </Paper>

          <Paper sx={{ flex: '1 1 calc(50% - 12px)', minWidth: { xs: '100%', md: 400 }, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              ตัวแทนขายดีเด่น (Top 5)
            </Typography>
            {topAgentsData.length > 0 ? (
              <Box sx={{ height: { xs: 220, md: 300 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topAgentsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'premium') {
                        return [formatCurrency(value) + ' บาท', 'เบี้ยประกัน'];
                      }
                      return [formatNumber(value), 'จำนวนกรมธรรม์'];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="policies" fill="#8884d8" name="จำนวนกรมธรรม์" />
                </BarChart>
              </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">ไม่มีข้อมูล</Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Sales by Company Section */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            ยอดขายตามบริษัทประกัน (Top 10)
          </Typography>
          {salesByCompanyData.length > 0 ? (
            <Box>
              <ResponsiveContainer width="100%" height={Math.max(200, salesByCompanyData.length * 44)}>
                <BarChart
                  layout="vertical"
                  data={salesByCompanyData}
                  margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      formatNumber(value) + ' รายการ',
                      name === 'life' ? 'ประกันชีวิต' : name === 'non_life' ? 'ประกันวินาศภัย' : name,
                    ]}
                    labelFormatter={(label) => {
                      const found = salesByCompanyData.find((d) => d.name === label);
                      return found?.fullName || label;
                    }}
                  />
                  <Legend
                    formatter={(value) =>
                      value === 'life' ? 'ประกันชีวิต' : value === 'non_life' ? 'ประกันวินาศภัย' : value
                    }
                  />
                  <Bar dataKey="life" name="life" stackId="a" fill={COLORS[0]} />
                  <Bar dataKey="non_life" name="non_life" stackId="a" fill={COLORS[1]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Summary table */}
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>บริษัทประกัน</strong></TableCell>
                      <TableCell align="center">ประกันชีวิต</TableCell>
                      <TableCell align="center">ประกันวินาศภัย</TableCell>
                      <TableCell align="center"><strong>รวม</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesByCompanyRaw.slice(0, 10).map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{item.company}</TableCell>
                        <TableCell align="center">{item.by_type['life'] || 0}</TableCell>
                        <TableCell align="center">{item.by_type['non_life'] || 0}</TableCell>
                        <TableCell align="center"><strong>{item.total}</strong></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">ยังไม่มีข้อมูลบริษัทประกัน (สถานะ "ซื้อ/ตอบรับ")</Typography>
            </Box>
          )}
        </Paper>

        {/* Recent Quotations Table */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              ใบเสนอราคาล่าสุด
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/quotations')}
            >
              ดูทั้งหมด
            </Button>
          </Box>
          {recentQuotations.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>เลขที่</TableCell>
                    <TableCell>ลูกค้า</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>ประเภท</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>ประกัน</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>รายละเอียด</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>วันที่</TableCell>
                    <TableCell align="center">สถานะ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentQuotations.map((q) => (
                    <TableRow
                      key={q.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/quotations/${q.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {q.quote_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{q.customer_name}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={getCustomerTypeLabel(q.customer_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{getInsuranceTypeLabel(q.insurance_type)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {q.details}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {q.quote_date ? new Date(q.quote_date).toLocaleDateString('th-TH') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusLabel(q.status)}
                          color={getStatusColor(q.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">ยังไม่มีใบเสนอราคา</Typography>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => navigate('/quotations/new')}
              >
                สร้างใบเสนอราคาใหม่
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default DashboardPage;
